import { useState, useEffect } from 'react'
import './App.css'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { NotificationsProvider } from './contexts/NotificationsContext'
import { ThemeProvider } from './components/theme-provider'
import { PlaidProvider } from './contexts/PlaidContext'
import Login from './components/auth/Login'
import SignUp from './components/auth/SignUp'
import PrivateRoute from './components/PrivateRoute'
import ResetPassword from './components/auth/ResetPassword'
import MainPage from './pages/MainPage'
import CalendarPage from './pages/CalendarPage'
import NotificationsPage from './pages/NotificationsPage'
import HelpPage from './pages/HelpPage'
import DashboardPage from './pages/DashboardPage'
import SettingsPage from './pages/SettingsPage'
import { PlacesPage } from './pages/PlacesPage'
import BillingPage from './pages/BillingPage'
import UpgradePage from './pages/UpgradePage'
import AccountPage from './pages/AccountPage'
import TransactionsPage from './pages/TransactionsPage'
import SubscriptionsPage from './pages/SubscriptionsPage'
import BudgetsOverviewPage from './app/(authenticated)/budgets/overview/page'
import BudgetsPage from './pages/BudgetsPage'
import AppLayout from './components/AppLayout'
import { AppPage } from './types/navigation'
import { isProtectedPage } from './routes/routes'

function AppContent() {
  const [currentPage, setCurrentPage] = useState<AppPage>('main')
  const { currentUser, loading } = useAuth()

  // Set initial page based on auth state
  useEffect(() => {
    if (!loading && currentUser) {
      setCurrentPage('dashboard')
    }
  }, [currentUser, loading])

  const navigateTo = (page: AppPage) => {
    setCurrentPage(page);
  }

  // Render content based on current page
  const renderProtectedContent = () => {
    switch(currentPage) {
      case 'dashboard':
        return <DashboardPage onNavigate={navigateTo} />;
      case 'calendar':
        return <CalendarPage onNavigate={navigateTo} />;
      case 'transactions':
        return <TransactionsPage onNavigate={navigateTo} />;
      case 'notifications':
        return <NotificationsPage onNavigate={navigateTo} />;
      case 'help':
        return <HelpPage onNavigate={navigateTo} />;
      case 'settings':
        return <SettingsPage onNavigate={navigateTo} />;
      case 'places':
        return <PlacesPage onNavigate={navigateTo} />;
      case 'billing':
        return <BillingPage onNavigate={navigateTo} />;
      case 'upgrade':
        return <UpgradePage onNavigate={navigateTo} />;
      case 'account':
        return <AccountPage onNavigate={navigateTo} />;
      case 'subscriptions':
        return <SubscriptionsPage onNavigate={navigateTo} />;
      case 'budgets-overview':
        return <BudgetsOverviewPage />;
      case 'budgets':
        return <BudgetsPage onNavigate={navigateTo} />;
      default:
        return null;
    }
  }

  return (
    <div className="app-container">
      {currentPage === 'main' && <MainPage onNavigate={navigateTo} />}
      {currentPage === 'login' && <Login onNavigate={navigateTo} />}
      {currentPage === 'signup' && <SignUp onNavigate={navigateTo} />}
      {currentPage === 'reset-password' && <ResetPassword onNavigate={navigateTo} />}
      {isProtectedPage(currentPage) && (
        <PrivateRoute onNavigate={navigateTo}>
          <AppLayout onNavigate={navigateTo} currentPage={currentPage}>
            {renderProtectedContent()}
          </AppLayout>
        </PrivateRoute>
      )}
    </div>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <NotificationsProvider>
        <ThemeProvider>
          <PlaidProvider>
            <AppContent />
          </PlaidProvider>
        </ThemeProvider>
      </NotificationsProvider>
    </AuthProvider>
  )
}
